import { useState, useEffect, useCallback, useRef } from "react";
import { typeAheadAdminService } from "@/services/type-ahead-admin.service";
import {
  CreateTypeAheadDto,
  TypeAhead,
  UpdateTypeAheadDto,
} from "@/types/typeAhead";
import { FilterDto, BackendSort } from "@/hooks/filterUtils";
import { FilterOperation, FilterType } from "@/types/filter";
import { filterKeyToConfigMap } from "@/config/typeAhead-table.config";
import { useToast } from "@/components/ui/utils/use-toast";
import { typeAheadService } from "@/services/type-ahead.service";

export const useTypeAheadAdmin = () => {
  const [data, setData] = useState<TypeAhead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<{
    key: keyof TypeAhead;
    direction: "asc" | "desc";
  } | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { toast } = useToast();
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const backendSort: BackendSort[] = [];
      if (sort) {
        backendSort.push({
          field: sort.key,
          order: sort.direction,
        });
      }

      const filterDto: FilterDto = {
        filters: {},
        sort: backendSort,
        pagination: { page, pageSize },
      };
      if (Object.keys(filters).length > 0) {
        filterDto.filters = Object.entries(filters).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              let operation = FilterOperation.STARTSWITH;
              let type = FilterType.STRING;
              const filterType = filterKeyToConfigMap[key];

              if (filterType === "select") {
                operation = FilterOperation.EQUAL;
                type = FilterType.NUMBER;
              }
              acc[key] = { operation, value, type };
            }
            return acc;
          },
          {}
        );
      }

      const response = await typeAheadAdminService.getAll(filterDto);
      const slicedData: TypeAhead[] = response.data.map((item) => ({
        ...item,
        values: item.values?.slice(0, 3) || [], // Fallback to [] if values is undefined
      }));
      setData(slicedData);
      setTotal(response.total);
    } catch (err) {
      console.error("Error fetching typehead categories:", err);
      setError("Failed to load typeahead categories. Please try again.");
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sort, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategory = async (id: string) => {
    try {
      return await typeAheadAdminService.getAll(id);
    } catch (err) {
      toast({
        title: "Error",
        description: "Error fetching categories.",
        variant: "destructive",
      });
      return null;
    }
  };

  const refreshData = () => {
    fetchData();
    setPage((current) => current);
  };

  const createTypeAhead = async (data: CreateTypeAheadDto) => {
    try {
      const addNewTypeAhead = await typeAheadAdminService.add(data);
      refreshData();
      return addNewTypeAhead;
    } catch (err) {
      toast({
        title: "Error",
        description: "Error creating typeahead.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateTypeAhead = async (data: UpdateTypeAheadDto) => {
    try {
      const result = await typeAheadAdminService.update(data);
      refreshData();
      return result;
    } catch (err) {
      toast({
        title: "Error",
        description: "Error updating typeahead.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteTypeAhead = async (id: string) => {
    try {
      await typeAheadAdminService.remove(id);
      toast({
        title: "Success",
        description: "Typeahead deleted.",
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: "Error deleting typeahead.",
        variant: "destructive",
      });
      throw err;
    }
  };

  return {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
    getCategory,
    createTypeAhead,
    updateTypeAhead,
    deleteTypeAhead,
  };
};

export function useTypeAhead(initialCategory?: string) {
  const [values, setValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(
    initialCategory || null
  );
  // Cache: category -> values
  const cacheRef = useRef<{ [cat: string]: string[] }>({});

  const fetch = useCallback(
    async (force = false) => {
      // Use cache unless force is true
      if (!force && cacheRef.current[category]) {
        setValues(cacheRef.current[category]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (!category) {
          return;
        }
        const data = await typeAheadService.getForCategory(category);
        setValues(data);
        cacheRef.current[category] = data;
      } catch (err: any) {
        setError(err?.message || "Failed to fetch type ahead values");
      } finally {
        setLoading(false);
      }
    },
    [category]
  );

  useEffect(() => {
    if (category) {
      fetch();
    }
  }, [category, fetch]);

  const add = useCallback(
    async (value: string) => {
      setLoading(true);
      setError(null);
      try {
        await typeAheadService.addToTypeAhead(category, value);
        await fetch(true); // force refresh cache
      } catch (err: any) {
        setError(err?.message || "Failed to add type ahead value");
      } finally {
        setLoading(false);
      }
    },
    [category, fetch]
  );

  return { values, loading, error, fetch, add, category, setCategory };
}
